import React, { useState, useEffect } from 'react';
import { Form, Select, Row, Col, Divider, Button, Table, Space, Input, DatePicker, InputNumber, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/es'; // o el idioma que corresponda
import axios from 'axios';
import dayjs from 'dayjs';
const { Option } = Select;

const PaymentTypesSelector = ({ paymentTypes, selectedPaymentTypes, setSelectedPaymentTypes, user }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch banks from API
  useEffect(() => {
    const fetchBanks = async () => {
      setLoading(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/banco`, config);
        setBanks(response.data);
      } catch (error) {
        console.error('Error fetching banks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanks();
  }, [user]);

  const handleAddPayment = () => {
    if (paymentTypes.length > 0) {
      const newPayment = {
        id: Date.now(), // Temporary ID for frontend tracking
        pag_tipopago: paymentTypes[0].tpa_id,
        tpa_nombre: paymentTypes[0].tpa_nombre,
        pag_importe: 0,
        pag_referencia: '',
        pag_fecha: null,
        pag_banco: null,
        ban_nombre: '',
        pag_comprobante: '',
        pag_fechaorigen: null,
        pag_fechavencimiento: null,
        pag_acreditado: false
      };
      setSelectedPaymentTypes([...selectedPaymentTypes, newPayment]);
    }
  };

  // Remove a payment type from the list
  const handleRemovePayment = (paymentId) => {
    const updatedPayments = selectedPaymentTypes.filter(payment =>
      payment.id !== paymentId && payment.pag_id !== paymentId
    );
    setSelectedPaymentTypes(updatedPayments);
  };

  // Update a specific payment type's details
  const handleUpdatePayment = (paymentId, field, value) => {
    const updatedPayments = selectedPaymentTypes.map(payment => {
      if ((payment.id === paymentId) || (payment.pag_id === paymentId)) {
        const updatedPayment = { ...payment, [field]: value };

        // If updating payment type, also update its name
        if (field === 'pag_tipopago') {
          const selectedType = paymentTypes.find(type => type.tpa_id === value);
          if (selectedType) {
            updatedPayment.tpa_nombre = selectedType.tpa_nombre;
          }
        }

        // If updating bank, also update its name
        if (field === 'pag_banco') {
          const selectedBank = banks.find(bank => bank.ban_id === value);
          if (selectedBank) {
            updatedPayment.ban_nombre = selectedBank.ban_nombre;
          }
        }

        return updatedPayment;
      }
      return payment;
    });

    setSelectedPaymentTypes(updatedPayments);
  };

  // Define table columns
  const columns = [
    {
      title: 'Tipo de Pago',
      dataIndex: 'pag_tipopago',
      key: 'pag_tipopago',
      width: '13%',
      render: (tpa_id, record) => (
        <Select
          style={{ width: '100%' }}
          value={tpa_id || record.pag_tipopago}
          onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_tipopago', value)}
        >
          {paymentTypes.map(type => (
            <Option key={type.tpa_id} value={type.tpa_id}>
              {type.tpa_nombre}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Monto',
      dataIndex: 'pag_importe',
      key: 'pag_importe',
      width: '13%',
      render: (amount, record) => (
        <InputNumber
          style={{ width: '100%' }}
          value={amount || record.pag_importe}
          min={0}
          step={0.01}
          precision={2}
          onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_importe', value)}
        />
      ),
    },
    {
      title: 'Banco',
      dataIndex: 'pag_banco',
      key: 'pag_banco',
      width: '13%',
      render: (ban_id, record) => (
        <Select
          style={{ width: '100%' }}
          value={ban_id || record.pag_banco}
          onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_banco', value)}
          allowClear
          placeholder="Seleccionar banco"
        >
          {banks.map(bank => (
            <Option key={bank.ban_id} value={bank.ban_id}>
              {bank.ban_nombre}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Num. Comprobante',
      dataIndex: 'pag_comprobante',
      key: 'pag_comprobante',
      width: '13%',
      render: (reference, record) => (
        <Input
          value={reference || record.pag_referencia}
          onChange={(e) => handleUpdatePayment(record.id || record.pag_id, 'pag_comprobante', e.target.value)}
          placeholder="Número/Referencia"
        />
      ),
    },
    {
      title: 'F. Origen',
      dataIndex: 'pag_fechaorigen',
    
      render: (date, record) => {
    
        let dateValue = date;
        
        if(date) {
          dateValue = dayjs(date);
        }
    
        return (
          <DatePicker
            value={dateValue}
            format={'DD/MM/YYYY'}
            onChange={(value) => {
    
              let formattedValue;
    
              if(value) {
                formattedValue = value.format('YYYY-MM-DD');
              }
    
              handleUpdatePayment(
                record.id,
                'pag_fechaorigen',
                formattedValue
              );
            }}
          />
        );
      }
    },
    
    {
      title: 'F. Vencimiento',
      dataIndex: 'pag_fechavencimiento',
    
      render: (date, record) => {
    
        let dateValue = date;
    
        if(date) {    
          dateValue = dayjs(date);
        }
    
        return (
          <DatePicker 
            value={dateValue}
            format={'DD/MM/YYYY'}
            onChange={(value) => {
        
              let formattedValue;
        
              if(value) {
                formattedValue = value.format('YYYY-MM-DD');
              }
        
              handleUpdatePayment(
                record.id, 
                'pag_fechavencimiento',
                formattedValue
              );
            }}
          />
        );
      }
    },
    {
      title: 'Acred.',
      dataIndex: 'pag_acreditado',
      key: 'pag_acreditado',
      width: 30, // Se le da un tamaño fijo menor para no ocupar tanto espacio
      align: 'center',
      render: (acreditado, record) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Checkbox
            checked={acreditado || record.pag_acreditado}
            onChange={(e) => handleUpdatePayment(record.id || record.pag_id, 'pag_acreditado', e.target.checked)}
          />
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 30, // Tamaño fijo para el botón de eliminar
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemovePayment(record.id || record.pag_id)}
          >
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Divider orientation="left">Tipos de Pago</Divider>

      <Row gutter={16}>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={selectedPaymentTypes.map(payment => ({
              ...payment,
              key: payment.id || payment.pag_id
            }))}
            pagination={false}
            bordered
            loading={loading}
            size="middle"
          />

          {/* Hidden form field to maintain compatibility with form submission */}
          <Form.Item
            name="paymentTypes"
            hidden
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Button
            type="dashed" // Cambiado para coincidir con "Agregar Movimiento"
            onClick={handleAddPayment}
            style={{ width: '100%', marginTop: 16, marginBottom: 16 }} // Estilo unificado
            icon={<PlusOutlined />}
            disabled={loading || paymentTypes.length === 0}
          >
            Agregar Método de Pago
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default PaymentTypesSelector;